import { useState, useEffect } from 'react';
import { contactController } from '../../controllers/contactController';
import Contact from '../../models/Contact';
import { Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', mobile: '',
    company: '', jobTitle: '', department: '', status: 'Active', type: 'Customer',
    address: '', city: '', state: '', country: 'India', notes: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchContacts(); }, []);

  useEffect(() => {
    let result = [...contacts];
    if (searchQuery) {
      result = result.filter(c => 
        c.getFullName().toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.company.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (filterStatus !== 'All') result = result.filter(c => c.status === filterStatus);
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'fullName') { aVal = a.getFullName(); bVal = b.getFullName(); }
      if (typeof aVal === 'string') return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    setFilteredContacts(result);
    setCurrentPage(1);
  }, [contacts, searchQuery, filterStatus, sortField, sortDirection]);

  const fetchContacts = async () => {
    setLoading(true);
    const result = await contactController.getAll();
    if (result.success) setContacts(result.data);
    else alert('Error: ' + result.message);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({ firstName: '', lastName: '', email: '', phone: '', mobile: '', company: '', jobTitle: '', department: '', status: 'Active', type: 'Customer', address: '', city: '', state: '', country: 'India', notes: '' });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    const result = await contactController.create(formData);
    if (result.success) { setShowAddModal(false); fetchContacts(); alert('✅ Contact created!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setFormData({ ...contact });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    const result = await contactController.update(selectedContact.id, formData);
    if (result.success) { setShowEditModal(false); fetchContacts(); alert('✅ Contact updated!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleView = (contact) => { setSelectedContact(contact); setShowViewModal(true); };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete ${contact.getFullName()}?`)) return;
  
    const result = await contactController.delete(contact.id);
  
    if (result.success) {
      fetchContacts();
      window.alert('✅ Contact deleted!');
    } else {
      window.alert('Error: ' + result.message);
    }
  };
  

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContacts = filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  const stats = {
    total: contacts.length,
    active: contacts.filter(c => c.status === 'Active').length,
    inactive: contacts.filter(c => c.status === 'Inactive').length,
    customers: contacts.filter(c => c.type === 'Customer').length
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '80px' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Contacts Management</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }}><Download size={18} /> Export</button>
          <button style={styles.button} onClick={handleAdd}><Plus size={18} /> Add Contact</button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}><div style={styles.statLabel}>Total Contacts</div><div style={styles.statValue}>{stats.total}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Active</div><div style={{ ...styles.statValue, color: '#10b981' }}>{stats.active}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Inactive</div><div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.inactive}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Customers</div><div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.customers}</div></div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input type="text" placeholder="Search contacts..." style={styles.searchInput} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option><option value="Active">Active</option><option value="Inactive">Inactive</option><option value="Pending">Pending</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
        ) : currentContacts.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No contacts found.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('fullName')}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Name {sortField === 'fullName' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}</div></th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Job Title</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td style={styles.td} onClick={() => handleView(contact)}><div style={{ fontWeight: '500' }}>{contact.getFullName()}</div></td>
                    <td style={styles.td}>{contact.email}</td>
                    <td style={styles.td}>{contact.phone || contact.mobile}</td>
                    <td style={styles.td}>{contact.company}</td>
                    <td style={styles.td}>{contact.jobTitle}</td>
                    <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: `${contact.getStatusColor()}20`, color: contact.getStatusColor() }}>{contact.status}</span></td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={styles.actionButton} onClick={() => handleView(contact)}><Eye size={18} color="#64748b" /></button>
                        <button style={styles.actionButton} onClick={() => handleEdit(contact)}><Edit2 size={18} color="#3b82f6" /></button>
                        <button style={styles.actionButton} onClick={() => handleDelete(contact)}><Trash2 size={18} color="#ef4444" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredContacts.length)} of {filteredContacts.length}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center' }}>Page {currentPage} of {totalPages}</div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add Contact' : 'Edit Contact'}</h2>
              <button style={styles.actionButton} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}><X size={20} /></button>
            </div>
            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (<div style={styles.errorList}>{formErrors.map((err, i) => <div key={i} style={styles.errorItem}><AlertCircle size={14} style={{ display: 'inline', marginRight: '8px' }} />{err}</div>)}</div>)}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}><label style={styles.label}>First Name *</label><input type="text" name="firstName" style={styles.input} value={formData.firstName} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Last Name *</label><input type="text" name="lastName" style={styles.input} value={formData.lastName} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Email *</label><input type="email" name="email" style={styles.input} value={formData.email} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Phone</label><input type="tel" name="phone" style={styles.input} value={formData.phone} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Mobile</label><input type="tel" name="mobile" style={styles.input} value={formData.mobile} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Company</label><input type="text" name="company" style={styles.input} value={formData.company} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Job Title</label><input type="text" name="jobTitle" style={styles.input} value={formData.jobTitle} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Department</label><input type="text" name="department" style={styles.input} value={formData.department} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Status</label><select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}><option value="Active">Active</option><option value="Inactive">Inactive</option><option value="Pending">Pending</option></select></div>
                  <div style={styles.formGroup}><label style={styles.label}>Type</label><select name="type" style={styles.input} value={formData.type} onChange={handleInputChange}><option value="Customer">Customer</option><option value="Partner">Partner</option><option value="Vendor">Vendor</option><option value="Prospect">Prospect</option></select></div>
                </div>
                <div style={styles.formGroup}><label style={styles.label}>Notes</label><textarea name="notes" style={styles.textarea} value={formData.notes} onChange={handleInputChange} /></div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>Cancel</button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>{formSubmitting ? 'Saving...' : (showAddModal ? 'Create' : 'Update')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedContact && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Contact Details</h2>
              <button style={styles.actionButton} onClick={() => setShowViewModal(false)}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Full Name</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedContact.getFullName()}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedContact.email}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Phone</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedContact.phone || 'N/A'}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Mobile</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedContact.mobile || 'N/A'}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Company</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedContact.company || 'N/A'}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Job Title</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedContact.jobTitle || 'N/A'}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div><span style={{ ...styles.badge, backgroundColor: `${selectedContact.getStatusColor()}20`, color: selectedContact.getStatusColor() }}>{selectedContact.status}</span></div>
              </div>
              {selectedContact.notes && (<div style={{ marginTop: '24px' }}><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Notes</div><div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '14px' }}>{selectedContact.notes}</div></div>)}
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedContact); }}><Edit2 size={16} /> Edit</button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;